/**
 * The Jev model record ⇄ the legacy `settings.scoring` group (models.md §9.2 step 5, §9.3, §9.6). Pure.
 *
 * Data schema 2 (quick controls) kept Jev's switches, the person's daily budget and the scoring text length
 * in the synced `settings` row `scoring`: { enabled, scoreFiltered, dailyLimit, bodyChars }. From data schema 3
 * the truth is the `models` row 'jev'. `legacyJev()` is the ONE place that reads the legacy shape: the
 * migration derives the Jev record from the settings register with it, and `settings.set('scoring', …)`
 * writes through with it. `scoringSettingsOf()` is the inverse that `settings.get().scoring` shows.
 *
 * Mapping (every replica computes the same row from the same register — keep it free of local config):
 *   enabled          → the record's switch (`disabled_reason = 'user'` when off)
 *   scoreFiltered    → the run rule: triggers "a message comes to rest in any folder by automation" + "on
 *                      request"; condition `folder not-in [dismissed]` (true: what schema 2 scored — arrivals
 *                      and what filters placed, never what a filter dismissed) or `folder in [inbox]` (false)
 *   dailyLimit       → options.dailyBudget (null = the plan's maximum)
 *   bodyChars        → options.bodyMaxChars: absent → 1,000 (the deployed default); null ("everything") →
 *                      6,000 (the default of scoring.body_max_chars, the ceiling); a number → that number
 *   topicMinScore    → 15 (schema 2's scoring.topic_min_score default; it was never a setting)
 *   topicFields      → ['name'] (Jev reads the name only, models.md §5.0)
 */

import type { FolderId } from '../contracts/ids.js';
import type { ModelOptions, ModelRun } from '../contracts/models.js';
import type { ScoringSettings } from '../contracts/settings.js';
import { ENGINE_CONFIG_DEFAULTS } from './config-defaults.js';
import { JEV_CATALOG_ENTRY } from './model-catalog.js';

/** What the legacy group determines of the Jev record. */
export interface LegacyJevRecord {
  enabled: boolean;
  disabledReason: 'user' | null;
  run: ModelRun;
  options: ModelOptions;
}

const DEFAULTS = JEV_CATALOG_ENTRY.defaults.options;
/** "Everything" in schema 2 (`bodyChars: null`) = the ceiling's default. */
export const LEGACY_ALL_BODY_CHARS: number = ENGINE_CONFIG_DEFAULTS['scoring.body_max_chars'];
const LEGACY_TOPIC_MIN_SCORE: number = ENGINE_CONFIG_DEFAULTS['scoring.topic_min_score'];

/** Jev's run rule for the legacy "Also score messages placed by filters" switch. */
export function legacyJevRun(scoreFiltered: boolean): ModelRun {
  return {
    triggers: [{ on: 'entry', folderId: 'any', by: 'automatic' }, { on: 'request' }],
    condition: {
      type: 'group',
      operator: 'all',
      children: [
        scoreFiltered
          ? { type: 'condition', field: 'folder', operator: 'not-in', values: ['dismissed' as FolderId] }
          : { type: 'condition', field: 'folder', operator: 'in', values: ['inbox' as FolderId] },
      ],
    },
  };
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const wholeOrNull = (v: unknown): number | null | undefined => (v === null ? null : typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : undefined);

/** The Jev record a legacy `settings.scoring` value stands for (unknown input: a stored JSON value, or nothing). */
export function legacyJev(value: unknown): LegacyJevRecord {
  const v = isRecord(value) ? value : {};
  const enabled = typeof v.enabled === 'boolean' ? v.enabled : true;
  const scoreFiltered = typeof v.scoreFiltered === 'boolean' ? v.scoreFiltered : true;
  const budget = wholeOrNull(v.dailyLimit);
  const body = wholeOrNull(v.bodyChars);
  return {
    enabled,
    disabledReason: enabled ? null : 'user',
    run: legacyJevRun(scoreFiltered),
    options: {
      bodyMaxChars: body === undefined || body === 0 ? DEFAULTS.bodyMaxChars : body === null ? LEGACY_ALL_BODY_CHARS : body,
      topicMinScore: LEGACY_TOPIC_MIN_SCORE,
      dailyBudget: budget === undefined ? null : budget,
      topicFields: ['name'],
    },
  };
}

/** True when the run rule only looks at Inbox (the legacy `scoreFiltered = false`). */
function runLimitedToInbox(run: ModelRun): boolean {
  const kids = run.condition.children;
  return kids.length === 1 && kids[0]?.type === 'condition' && kids[0].field === 'folder' && kids[0].operator === 'in'
    && kids[0].values.length === 1 && kids[0].values[0] === 'inbox';
}

/**
 * `settings.get().scoring` as derived from the Jev record (models.md §9.3). `bodyChars` is null ("everything")
 * when the record sends at least `bodyMax` characters (scoring.body_max_chars).
 */
export function scoringSettingsOf(jev: { enabled: boolean; run: ModelRun; options: ModelOptions }, bodyMax: number): ScoringSettings {
  return {
    enabled: jev.enabled,
    scoreFiltered: !runLimitedToInbox(jev.run),
    dailyLimit: jev.options.dailyBudget,
    bodyChars: jev.options.bodyMaxChars >= bodyMax ? null : jev.options.bodyMaxChars,
  };
}
